import {
  Component,
  ElementRef,
  EventEmitter,
  ContentChildren,
  Host,
  HostBinding,
  Inject,
  Input,
  AfterContentInit,
  Optional,
  Output,
  QueryList,
} from '@angular/core';

import { LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { Direction } from '../common/direction';
import { Orientation } from '../common/orientation';

import { SplitterPaneComponent } from './splitter-pane.component';

import { SplitterService } from './splitter.service';

/**
 * Represents the Kendo UI Splitter component for Angular.
 *
 * ```ts-preview
 *
 *  @Component({
 * standalone: false,
 *    selector: 'my-app',
 *    template: `
 *        <kendo-splitter style="height: 23.33rem;">
 *
 *          <kendo-splitter-pane [collapsible]="true" size="30%">
 *            <h3>Inner splitter / left pane</h3>
 *            <p>Resizable and collapsible.</p>
 *          </kendo-splitter-pane>
 *
 *          <kendo-splitter-pane>
 *            <h3>Inner splitter / center pane</h3>
 *            <p>Resizable only.</p>
 *          </kendo-splitter-pane>
 *
 *          <kendo-splitter-pane [collapsible]="true" size="30%">
 *            <h3>Inner splitter / right pane</h3>
 *            <p>Resizable and collapsible.</p>
 *          </kendo-splitter-pane>
 *
 *        </kendo-splitter>
 *      `,
 *    styles: [ `
 *        h3 { font-size: 1.2em; }
 *        h3, p { margin: 0.833rem; padding: 0; }
 *    ` ]
 *  })
 *  class AppComponent {}
 * ```
 */
@Component({
  exportAs: 'kendoSplitter',
  standalone: false,
  selector: 'kendo-splitter',
  providers: [
    SplitterService,
    LocalizationService,
    {
      provide: L10N_PREFIX,
      useValue: 'kendo.spliter',
    },
  ],
  template: `
    <ng-content select="kendo-splitter-pane"></ng-content>
    <ng-container *ngFor="let pane of panes; let index = index; let last = last">
      <kendo-splitter-bar kendoLayoutDraggable *ngIf="!last" [index]="index" [orientation]="orientation">
      </kendo-splitter-bar>
    </ng-container>
  `,
})
export class SplitterComponent implements AfterContentInit {
  /**
   * Specifies the orientation of the panes within the Splitter.
   * Panes in a horizontal Splitter are placed horizontally.
   * Panes in a vertical Splitter are placed vertically.
   */
  @Input()
  public orientation: Orientation = 'horizontal';

  /**
   * Fires after a Splitter pane is resized or collapsed.
   * Useful for triggering layout calculations on components
   * which are positioned inside the panes.
   */
  @Output()
  public layoutChange: EventEmitter<string>;

  @HostBinding('class.k-widget')
  @HostBinding('class.k-splitter')
  @HostBinding('class.k-splitter-flex')
  public get hostClasses(): boolean {
    return true;
  }

  @HostBinding('class.k-splitter-horizontal')
  public get horizontalHostClasses(): boolean {
    return this.orientation === 'horizontal';
  }
  @HostBinding('class.k-splitter-vertical')
  public get verticalHostClasses(): boolean {
    return this.orientation === 'vertical';
  }

  @HostBinding('attr.dir') get dir(): string {
    return this.direction;
  }

  @HostBinding('attr.role')
  public ariaRole: string = 'splitter';

  /**
   * @hidden
   */
  @ContentChildren(SplitterPaneComponent)
  public panes: QueryList<SplitterPaneComponent>;

  constructor(
    protected element: ElementRef,
    protected splitterService: SplitterService,
    private localization: LocalizationService,
    @Optional() @Host() @Inject(SplitterPaneComponent) enclosingPane?: SplitterPaneComponent,
  ) {
    if (enclosingPane) {
      enclosingPane.containsSplitter = true;
    }

    // the handler only runs in NgZone if there are bound handlers
    // this line merges both streams
    this.layoutChange = this.splitterService.layoutChange;
  }

  public ngAfterContentInit(): void {
    const reconfigure = () => {
      this.splitterService.configure({
        panes: this.panes.toArray(),
        orientation: this.orientation,
        containerSize: () => {
          if (this.orientation === 'vertical') {
            return this.element.nativeElement.offsetHeight;
          } else {
            return this.element.nativeElement.offsetWidth;
          }
        },
      });
    };

    reconfigure();

    this.panes.changes.subscribe(reconfigure);
  }

  private get direction(): Direction {
    return this.localization.rtl ? 'rtl' : 'ltr';
  }
}
